import { useEffect, useState } from 'react';
import { CalendarPlus, X } from 'lucide-react';
import { useI18n } from '../../context/useI18n';
import { ADMIN_FORM_MODAL_PANEL } from './adminModalPanel';

const DAY_PRESETS = [-30, -15, -7, 7, 15, 30, 60, 90];

export default function ExtendVigenciaModal({
  open,
  userName = '',
  planName = '',
  moduleLabel = 'D28D',
  saving = false,
  onClose,
  onConfirm,
}) {
  const { t } = useI18n();
  const [days, setDays] = useState('30');

  useEffect(() => {
    if (open) setDays('30');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const parsedDays = Number(days);
  const valid = Number.isFinite(parsedDays) && parsedDays !== 0 && parsedDays >= -365 && parsedDays <= 365;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    onConfirm?.(parsedDays);
  };

  return (
    <div
      className="form-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="extend-vigencia-title"
      onClick={() => !saving && onClose?.()}
    >
      <div className={`${ADMIN_FORM_MODAL_PANEL} max-w-md`} onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-lime-500/15 text-lime-700 shrink-0">
              <CalendarPlus size={18} />
            </span>
            <div className="min-w-0">
              <h4 id="extend-vigencia-title" className="font-semibold text-stone-900 truncate">
                {t('vigencias.extend_title', 'Ampliar vigencia')}
              </h4>
              <p className="text-xs text-stone-500 truncate">
                {moduleLabel}
                {planName ? ` · ${planName}` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="form-close-btn"
            onClick={onClose}
            disabled={saving}
            aria-label={t('common.close', 'Cerrar')}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="form-modal-content space-y-4">
          {userName && (
            <p className="text-sm text-stone-600">
              {t('vigencias.extend_for_user', 'Usuario')}:{' '}
              <span className="font-semibold text-stone-900">{userName}</span>
            </p>
          )}

          <div>
            <label className="label" htmlFor="extend-days-presets">
              {t('vigencias.days_quick', 'Días rápidos')}
            </label>
            <div id="extend-days-presets" className="flex flex-wrap gap-2 mt-1">
              {DAY_PRESETS.map((preset) => {
                const selected = days === String(preset);
                return (
                  <button
                    key={preset}
                    type="button"
                    disabled={saving}
                    className={`min-w-[3rem] px-3 py-1.5 rounded-xl text-sm font-medium ${
                      selected ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setDays(String(preset))}
                  >
                    {preset > 0 ? `+${preset}d` : `${preset}d`}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="label" htmlFor="extend-days-input">
              {t('vigencias.days_custom', 'Días personalizados')}
            </label>
            <input
              id="extend-days-input"
              type="number"
              min={-365}
              max={365}
              className="input w-full"
              value={days}
              disabled={saving}
              onChange={(e) => setDays(e.target.value)}
              placeholder={t('vigencias.days_placeholder', 'Ej. 30')}
            />
            <p className="text-xs text-stone-500 mt-1">
              {t('vigencias.days_hint', 'Entre -365 y 365. Se suman o restan al corte actual de este módulo (no trunca el otro producto).')}
            </p>
          </div>

          <div className="form-modal-footer -mx-5 -mb-5 mt-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={!valid || saving}>
              <CalendarPlus size={16} />
              {saving
                ? t('common.saving', 'Guardando...')
                : t('vigencias.extend_confirm', 'Aplicar {n} días').replace('{n}', String(valid ? parsedDays : '—'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
